// Core
import { useEffect, useState } from 'react';
import {
  Box,
  Image,
  Divider,
  Button,
  Grid,
  Text,
  Flex,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

// Assets
import { formatDateTime, parseFullDate } from 'services/Utils';
import placeholderGiveaways from 'assets/img/placeholder-giveaways.jpg';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import { ReactComponent as Prize1 } from 'assets/svg/prizes/prize-1.svg';
import { ReactComponent as Prize2 } from 'assets/svg/prizes/prize-2.svg';
import { ReactComponent as Prize3 } from 'assets/svg/prizes/prize-3.svg';
import { ReactComponent as DailyPrize } from 'assets/svg/prizes/daily.svg';
import { ReactComponent as PlaceholderPrize } from 'assets/svg/prizes/placeholder.svg';
import { ReactComponent as EntriesCancellingIcon } from 'assets/svg/entries-cancelling.svg';

// Components
import Card from 'components/Card/Card';
import Countdown from 'components/Timers/Countdown';
import { StateGiveawayEndingIcon, StateGiveawayOpeningIcon } from 'components/Icons/Icons';

const PrizeItem = ({ indexPrize, title, firstAndLastName }) => {
  return (
    <Flex key={indexPrize} gap="8px" mb="16px">
      <Box
        borderRadius="8px"
        bg="rgba(9, 155, 255, 0.10)"
        w="32px"
        h="32px"
        p="4px"
      >
        {indexPrize + 1 === 1 ? (
          <Prize1 />
        ) : indexPrize + 1 === 2 ? (
          <Prize2 />
        ) : indexPrize + 1 === 3 ? (
          <Prize3 />
        ) : (
          <PlaceholderPrize style={{ width: '24px', height: '24px' }} />
        )}
      </Box>

      <Box>
        <Text variant="title-md" mb="4px">
          {indexPrize + 1}
          {indexPrize + 1 === 1
            ? 'st'
            : indexPrize + 1 === 2
            ? 'nd'
            : indexPrize + 1 === 3
            ? 'rd'
            : 'th'}{' '}
          Prize {title}
        </Text>
        <Text>{firstAndLastName}</Text>
      </Box>
    </Flex>
  );
};

const Giveaway = ({ item, type, userData }) => {
  const colorDate = useColorModeValue('black', '#9FABCB');
  const colorDivider = useColorModeValue(
    '1px solid #CBE7FF',
    '1px solid #262D47'
  );

  const giveawayStateStyles = {
    color: 'white',
    fontSize: '15px',
    fontStyle: 'italic',
    lineHeight: '22px',
    fontWeight: '700',
    letterSpacing: '-1px',
    textTransform: 'uppercase'
  };

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const history = useHistory();
  const handleClick = (event, id) => {
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.button === 1 ||
      item.promotionLink ||
      item.publicPromotionLink
    ) {
      return;
    }

    event.preventDefault();
    history.push(`/giveaways/${id}`);
  };

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isOpen = !(new Date(item?.startDate) > now);

  return (
    <Card
      padding="0"
      overflow="hidden"
      pointerEvents={isOpen ? 'unset' : 'none'}
      cursor={item?.status === 'finished' ? 'cursor' : 'pointer'}
    >
      {isOpen && (
        <a
          href={ userData ? item?.promotionLink : item?.publicPromotionLink ?? `/giveaways/${item?.id}`}
          onClick={(e) => handleClick(e, item?.id)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: item?.status === 'finished' ? 'none' : 'unset',
          }}
        />
      )}

      <Box position="relative">
        {type !== 'daily' &&
        <Image
          src={item?.image ?? placeholderGiveaways}
          w="100%"
          h={{ base: '172px' }}
          objectFit="cover"
        />
        }
        {(
          (type === 'upcoming' && new Date(item?.endDate) <= now)
          || 
          (type === 'upcoming-cancelling' && new Date(item?.endDate) <= now)
          ) && (
          <Countdown
            fontSize="15px"
            endDate={item?.endDate}
            declareDate={item?.declareDate}
            p="2px 6px"
          />)
        }

        {(
          (type === 'upcoming' && new Date(item?.endDate) > now)
          ||
          (type === 'upcoming-cancelling' && new Date(item?.endDate) > now)
        ) && (
          <Flex 
            position="relative" 
            borderRadius="30px"
            position="absolute"
            right="8px"
            top="8px"
            p="1px"
            bg={new Date(item?.startDate) > now ? 'linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)' : '#FF3B30'}
            gap="6px">
            <Flex position="relative" alignItems="center" gap="4px" pl="8px">
              { new Date(item?.startDate) > now ?
                <>
                  <StateGiveawayOpeningIcon fontSize="14px" />
                  <Text variant="title-md" style={giveawayStateStyles}>
                    Opening in:
                  </Text>
                </>
                :
                <>
                  <StateGiveawayEndingIcon fontSize="14px" />
                  <Text variant="title-md" style={giveawayStateStyles}>
                    Closing in:
                  </Text>
                </>
              }
            </Flex>

            { new Date(item?.startDate) > now ?
              <Countdown
                fontSize="15px"
                endDate={item?.startDate}
                declareDate={item?.startDate}
                borderWidth="0px"
                position="relative"
                top="0"
                p="2px 6px"
                right="0"
              /> 
              :
              <Countdown
                fontSize="15px"
                endDate={item?.endDate}
                declareDate={item?.declareDate}
                borderWidth="0px"
                position="relative"
                top="0"
                p="2px 6px"
                right="0"
              />
            }
          </Flex>
        )}
      </Box>

      <Box borderTop={ type !== 'daily' ? colorDivider : 'unset'} p="12px 12px 0px 12px">
        <Text variant="header" fontSize="16px" letterSpacing="0" isTruncated>
          {type !== 'daily' ? item?.title : '$1000 prize giveaway'}
        </Text>
        <Text
          variant="text-sm"
          fontWeight="500"
          color={colorDate}
          mt="8px"
          isTruncated
        >
          {(type === 'upcoming' || type === 'upcoming-cancelling')
            ? formatDateTime(item?.endDate)
            : parseFullDate(item?.endDate)}{' '}
          {type === 'winners' ? '- Event complete' : ''}
        </Text>

        { type === 'upcoming' && (
          <Box mb="12px">
            {userData && (
              <Text
                mt="18px"
                variant="title-md"
                fontSize="13px"
                borderRadius="full"
                p="2px 8px"
                width="max-content"
                bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                color="white"
                fontStyle="italic"
              >
                YOUR ENTRIES: {item?.entries}
              </Text>
            )}
          </Box>
        )}

        { type === 'upcoming-cancelling' && (
          <Box mb="12px">
            {userData && (
              <Flex
                alignItems="center"
                mt="18px"
                borderRadius="full"
                p="2px 8px"
                width="max-content"
                bg="linear-gradient(180deg, #FD2A2A 0%, #AF0000 100%), #FF3B30"
                gap="4px"
              >
                <EntriesCancellingIcon />
                <Text
                  variant="title-md"
                  fontSize="13px"
                  color="white"
                  fontStyle="italic"
                  >YOUR ENTRIES: {item?.entries}</Text>
              </Flex>
            )}
          </Box>
        )}

        {type === 'winners' && (
          <>
            <Divider borderBottom={colorDivider} my="12px" />

            {(() => {
              const defaultPrize = {
                title: '',
                firstAndLastName: '',
              };
              const prizesList = item?.prizes ? [...item.prizes] : [];
              const isFinished = item?.status === 'finished';

              return prizesList
                .filter((prize) => !isFinished || !!prize?.winner)
                .map((prize, indexPrize) => {
                  const displayName = prize?.winner
                    ? [prize.winner.firstName, prize.winner.lastName]
                        .filter(Boolean)
                        .join(' ')
                    : defaultPrize.firstAndLastName;

                  const formattedName =
                    prize?.winner && prize.winner.lastName
                      ? `${prize.winner.firstName || ''} ${prize.winner.lastName.charAt(
                          0,
                        )}.`.trim()
                      : displayName;

                  return (
                    <PrizeItem
                      key={indexPrize}
                      indexPrize={indexPrize}
                      title={prize?.title || defaultPrize?.title}
                      firstAndLastName={formattedName}
                    />
                  );
                });
            })()}
          </>
        )}

        {type === 'daily' && (
          <>
            <Divider borderBottom={colorDivider} my="12px" />

            <Flex gap="8px" mb="24px">
              <Box
                borderRadius="8px"
                bg="rgba(9, 155, 255, 0.10)"
                w="32px"
                h="32px"
                p="4px"
              >
                  <DailyPrize />
              </Box>

              <Box>
                <Text variant="title-md" mb="4px" textTransform="uppercase">Winner:</Text>
                <Text>
                  {item?.prizes[0]?.winner
                    ? (() => {
                        const { firstName, lastName } = item.prizes[0].winner;
                        const baseName = [firstName, lastName]
                          .filter(Boolean)
                          .join(' ');

                        return lastName
                          ? `${firstName || ''} ${lastName.charAt(0)}.`.trim()
                          : baseName;
                      })()
                    : 'Remaining prizes not awarded'}
                </Text>
              </Box>
            </Flex>
          </>
        )}

      </Box>

      {isDark ? (
        <Lines
          className="abstract-lines"
          style={{
            zIndex: 1,
            transform: 'scale(0.8)',
            left: '-50px',
            top: type !== 'upcoming' ? '50px' : '-50px',
            transform: 'scale(0.5) rotate(266deg)',
          }}
        />
      ) : (
        <LinesWhite
          className="abstract-lines"
          style={{
            opacity: '0.4',
            zIndex: 1,
            transform: 'scale(0.8)',
            left: '-50px',
            top: type !== 'upcoming' ? '50px' : '-50px',
            transform: 'scale(0.5) rotate(266deg)',
          }}
        />
      )}
    </Card>
  );
};

export default Giveaway;
